import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { TrackingService } from '@app/api/tracking.service';
import { UtilService } from '@app/services/util.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'meu-user-tracking',
  templateUrl: './user-tracking.component.html',
  styleUrls: ['./user-tracking.component.scss'],
})
export class UserTrackingComponent implements OnInit, OnDestroy {
  keyUp = new Subject<string>();
  lstLogs: any[] = [];
  topProducts: any[] = [];
  topFromDate = '';
  topToDate = '';

  displayedColumns: string[] = [
    'index',
    'customer',
    'product',
    'action',
    'device',
    'start_time',
    'duration',
  ];

  pageSizeOptions: number[] = [10, 20, 50, 100];
  total = 0;
  pageSize = 10;
  pageIndex = 1;
  filter = '';
  deviceFilter = '';
  actionFilter = '';

  historyPage = 1;
  historyPageSize = 10;
  isHistoryLoading = false;
  canLoadMoreHistory = true;

  isPanelOpen = false;
  selectedUserId = '';
  selectedUserName = '';
  userHistory: any[] = [];

  isProductPanelOpen = false;
  selectedProductId = '';
  selectedProductName = '';
  productHistory: any[] = [];
  productHistoryPage = 1;
  productHistoryPageSize = 10;
  isProductHistoryLoading = false;
  canLoadMoreProductHistory = true;

  constructor(
    private trackingService: TrackingService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit() {
    this.keyUp.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value) => {
      this.pageIndex = 1;
      this.filter = value;
      this.getListLogs();
    });
    this.getListLogs();

    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 3);
    this.topFromDate = from.toISOString().split('T')[0];
    this.topToDate = to.toISOString().split('T')[0];
    this.getTopProducts();
  }

  formatTime(seconds: number): string {
    if (seconds == null || seconds === undefined) {
      return '-';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  getTopProducts() {
    this.trackingService.apiProductViewTopViewedGet(10, this.topFromDate, this.topToDate).subscribe((res) => {
      if (res && res.success) {
        this.topProducts = res.data;
        this.cdr.detectChanges();
      }
    });
  }

  onTopDateChange() {
    this.getTopProducts();
  }

  getListLogs() {
    const sieveFilters = [];
    if (this.filter) {
      sieveFilters.push(`(product.name|user.full_name|user.email)@=*${this.filter}`);
    }
    if (this.actionFilter) {
      sieveFilters.push(`action_type==${this.actionFilter}`);
    }
    if (this.deviceFilter) {
      sieveFilters.push(`device_type==${this.deviceFilter}`);
    }
    const filterString = sieveFilters.join(',');

    this.trackingService
      .apiProductViewAllGet(filterString, '-created_at', this.pageIndex, this.pageSize)
      .subscribe((res) => {
        if (res && res.success) {
          this.lstLogs = res.data.collection;
          this.total = res.data.total;
          this.cdr.detectChanges();
        }
      });
  }

  onPageChange(pageNumber: number) {
    this.pageIndex = pageNumber;
    this.getListLogs();
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.getListLogs();
  }

  onActionFilterChange(event: any) {
    this.actionFilter = event.target.value;
    this.pageIndex = 1;
    this.getListLogs();
  }

  onDeviceFilterChange(event: any) {
    this.deviceFilter = event.target.value;
    this.pageIndex = 1;
    this.getListLogs();
  }

  openHistory(userId: string, userName: string) {
    if (!userId) {
      return;
    }
    this.selectedUserId = userId;
    this.selectedUserName = userName || 'Unknown';
    this.isPanelOpen = true;

    document.body.classList.add('modal-open');
    this.userHistory = [];
    this.historyPage = 1;
    this.canLoadMoreHistory = true;

    this.loadMoreHistory();
  }

  loadMoreHistory() {
    if (this.isHistoryLoading || !this.canLoadMoreHistory) {
      return;
    }

    this.isHistoryLoading = true;

    this.trackingService
      .apiProductViewUserUserIdGet(this.selectedUserId, this.historyPage, this.historyPageSize)
      .subscribe((res: any) => {
        if (res && res.success) {
          const newData = res.data.collection;

          this.userHistory = [...this.userHistory, ...newData];

          if (newData.length < this.historyPageSize) {
            this.canLoadMoreHistory = false;
          } else {
            this.historyPage++;
          }
        }
        this.isHistoryLoading = false;
        this.cdr.detectChanges();
      });
  }

  onPanelScroll(event: any) {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      this.loadMoreHistory();
    }
  }

  closePanel() {
    this.isPanelOpen = false;
    document.body.classList.remove('modal-open');
    this.cdr.detectChanges();
  }

  openProductHistory(productId: string, productName: string) {
    if (!productId) {
      return;
    }
    this.selectedProductId = productId;
    this.selectedProductName = productName || 'Sản phẩm';
    this.isProductPanelOpen = true;

    document.body.classList.add('modal-open');
    this.productHistory = [];
    this.productHistoryPage = 1;
    this.canLoadMoreProductHistory = true;

    this.loadMoreProductHistory();
  }

  loadMoreProductHistory() {
    if (this.isProductHistoryLoading || !this.canLoadMoreProductHistory) {
      return;
    }

    this.isProductHistoryLoading = true;

    this.trackingService
      .apiProductViewProductProductIdGet(this.selectedProductId, this.productHistoryPage, this.productHistoryPageSize)
      .subscribe((res: any) => {
        if (res && res.success) {
          const newData = res.data.collection;

          this.productHistory = [...this.productHistory, ...newData];

          if (newData.length < this.productHistoryPageSize) {
            this.canLoadMoreProductHistory = false;
          } else {
            this.productHistoryPage++;
          }
        }
        this.isProductHistoryLoading = false;
        this.cdr.detectChanges();
      });
  }

  onProductPanelScroll(event: any) {
    const element = event.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50) {
      this.loadMoreProductHistory();
    }
  }

  closeProductPanel() {
    this.isProductPanelOpen = false;
    document.body.classList.remove('modal-open');
    this.cdr.detectChanges();
  }

  routerLinkToProduct(productId: string, productName: string) {
    if (!productId) {
      return;
    }
    this.router.navigate([`/product/${productId}/${UtilService.slugify(productName || '')}/details`]);
  }

  ngOnDestroy() {
    document.body.classList.remove('modal-open');
  }
}
